import { ArrayVisualizer } from '../visualizers/ArrayVisualizer';

// 通用可视化器类型
export type AnyVisualizer = any;

/**
 * 动画步骤接口
 */
export interface AnimationStep {
  type: 'highlight' | 'compare' | 'swap' | 'move' | 'mark' | 'unmark';
  indices: number[];
  description: string;
  variables?: Record<string, unknown>;
}

/**
 * 动画调度器 - 管理算法动画的播放
 */
export class AnimationScheduler {
  private visualizer: AnyVisualizer | null = null;
  private visualizerType: 'array' | 'linked-list' | 'binary-tree' = 'array';
  private steps: AnimationStep[] = [];
  private currentStep = 0;
  private isPlaying = false;
  private speed = 1;
  private animationTimer: number | null = null;
  private onPlayStateChange: ((isPlaying: boolean, finished?: boolean) => void) | null = null;
  private stepHistory: Array<{step: number, description: string, variables?: Record<string, unknown>}> = [];

  /**
   * 设置播放状态变化回调
   */
  setOnPlayStateChange(callback: (isPlaying: boolean, finished?: boolean) => void): void {
    this.onPlayStateChange = callback;
  }

  /**
   * 设置可视化器
   */
  setVisualizer(visualizer: AnyVisualizer, type: 'array' | 'linked-list' | 'binary-tree' = 'array'): void {
    this.visualizer = visualizer;
    this.visualizerType = type;
  }

  /**
   * 设置动画步骤
   */
  setSteps(steps: AnimationStep[]): void {
    this.steps = steps;
    this.currentStep = 0;
    this.stepHistory = [];  // 清空历史
    this.clearHistoryDisplay();  // 清空观察窗显示
    this.updateStepDisplay();
  }

  /**
   * 设置播放速度
   */
  setSpeed(speed: number): void {
    this.speed = speed;
    
    // 如果正在播放，重新启动定时器
    if (this.isPlaying) {
      this.stopTimer();
      this.startTimer();
    }
  }

  /**
   * 开始播放
   */
  play(): void {
    if (this.isPlaying) return;
    
    this.isPlaying = true;
    this.startTimer();
  }

  /**
   * 暂停播放
   */
  pause(finished = false): void {
    this.isPlaying = false;
    this.stopTimer();
    
    // 通知UI更新状态
    if (this.onPlayStateChange) {
      this.onPlayStateChange(false, finished);
    }
  }

  /**
   * 重置
   */
  async reset(): Promise<void> {
    this.pause();
    this.currentStep = 0;
    this.stepHistory = [];  // 清空历史
    this.clearHistoryDisplay();  // 清空观察窗显示
    
    if (this.visualizer && typeof this.visualizer.reset === 'function') {
      try {
        await this.visualizer.reset();
      } catch (e) {
        console.warn('重置可视化器失败:', e);
      }
    }
    
    this.updateStepDisplay();
  }

  /**
   * 前进一步
   */
  async stepForward(): Promise<void> {
    if (this.currentStep >= this.steps.length) return;
    
    await this.executeStep(this.steps[this.currentStep]);
    this.currentStep++;
    this.updateStepDisplay();
  }

  /**
   * 后退一步
   */
  async stepBack(): Promise<void> {
    if (this.currentStep <= 0) return;
    
    this.currentStep--;
    
    // 重置并重放到当前步骤
    if (this.visualizer && typeof this.visualizer.reset === 'function') {
      try {
        await this.visualizer.reset();
        
        for (let i = 0; i < this.currentStep; i++) {
          await this.executeStep(this.steps[i], true);
        }
      } catch (e) {
        console.warn('后退失败:', e);
      }
    }
    
    this.updateStepDisplay();
  }

  /**
   * 跳到结束
   */
  async jumpToEnd(): Promise<void> {
    this.pause();
    
    while (this.currentStep < this.steps.length) {
      await this.executeStep(this.steps[this.currentStep], true);
      this.currentStep++;
    }
    
    this.updateStepDisplay();
  }

  /**
   * 启动定时器（使用递归 setTimeout 避免竞态条件）
   */
  private startTimer(): void {
    const interval = 1000 / this.speed;
    
    const scheduleNext = async () => {
      // 检查是否已停止或播放完成
      if (!this.isPlaying) return;
      
      if (this.currentStep >= this.steps.length) {
        this.pause(true);  // 播放完成
        return;
      }
      
      await this.stepForward();
      
      // 只有当前步骤完成后，才安排下一步
      if (this.isPlaying && this.currentStep < this.steps.length) {
        this.animationTimer = window.setTimeout(scheduleNext, interval);
      } else if (this.currentStep >= this.steps.length) {
        this.pause(true);  // 播放完成
      }
    };
    
    // 启动第一个定时器
    this.animationTimer = window.setTimeout(scheduleNext, interval);
  }

  /**
   * 停止定时器
   */
  private stopTimer(): void {
    if (this.animationTimer !== null) {
      clearTimeout(this.animationTimer);
      this.animationTimer = null;
    }
  }

  /**
   * 执行单个步骤
   */
  private async executeStep(step: AnimationStep, instant = false): Promise<void> {
    if (!this.visualizer) return;
    
    const duration = instant ? 0 : 500 / this.speed;
    
    try {
      switch (step.type) {
        case 'highlight':
          if (typeof this.visualizer.highlight === 'function') {
            await this.visualizer.highlight(step.indices[0], duration);
          }
          break;
        case 'compare':
          if (typeof this.visualizer.compare === 'function') {
            await this.visualizer.compare(step.indices[0], step.indices[1], duration);
          } else if (typeof this.visualizer.highlight === 'function') {
            await this.visualizer.highlight(step.indices[0], duration);
          }
          break;
        case 'swap':
          if (typeof this.visualizer.swap === 'function') {
            await this.visualizer.swap(step.indices[0], step.indices[1], duration);
          }
          break;
        case 'mark':
          if (typeof this.visualizer.markSorted === 'function') {
            await this.visualizer.markSorted(step.indices, duration);
          } else if (typeof this.visualizer.highlight === 'function') {
            await this.visualizer.highlight(step.indices[0], duration);
          }
          break;
        case 'unmark':
          if (typeof this.visualizer.unhighlight === 'function') {
            await this.visualizer.unhighlight(step.indices[0]);
          }
          break;
      }
    } catch (e) {
      console.warn('动画步骤执行失败:', e);
    }
    
    // 更新变量显示 - 添加到历史
    if (step.variables || step.description) {
      this.addToHistory(this.currentStep, step.description, step.variables);
    }
  }

  /**
   * 更新步骤显示
   */
  private updateStepDisplay(): void {
    const stepEl = document.getElementById('current-step');
    const descEl = document.getElementById('step-description');
    
    if (stepEl) {
      stepEl.textContent = `步骤: ${this.currentStep}/${this.steps.length}`;
    }
    
    if (descEl) {
      const desc = this.currentStep > 0 && this.currentStep <= this.steps.length
        ? this.steps[this.currentStep - 1].description
        : '准备就绪';
      descEl.textContent = desc;
    }
  }

  /**
   * 清空历史显示
   */
  private clearHistoryDisplay(): void {
    const varList = document.getElementById('variables-list');
    if (varList) {
      varList.innerHTML = '<div class="history-placeholder">暂无执行历史，点击播放开始</div>';
    }
  }

  /**
   * 添加步骤到历史记录
   */
  private addToHistory(stepNum: number, description: string, variables?: Record<string, unknown>): void {
    // 添加到历史数组
    this.stepHistory.push({ step: stepNum, description, variables });
    
    // 更新UI显示
    const varList = document.getElementById('variables-list');
    if (!varList) return;
    
    // 移除占位符
    const placeholder = varList.querySelector('.history-placeholder');
    if (placeholder) placeholder.remove();
    
    // 创建历史项
    const historyItem = document.createElement('div');
    historyItem.className = 'history-item';
    historyItem.setAttribute('data-step', stepNum.toString());
    
    // 步骤头部
    const header = document.createElement('div');
    header.className = 'history-header';
    header.innerHTML = `
      <span class="history-step">步骤 ${stepNum}</span>
      <span class="history-desc">${description || ''}</span>
    `;
    historyItem.appendChild(header);
    
    // 变量显示
    if (variables && Object.keys(variables).length > 0) {
      const varsContainer = document.createElement('div');
      varsContainer.className = 'history-vars';
      
      for (const [name, value] of Object.entries(variables)) {
        const varItem = document.createElement('div');
        varItem.className = 'history-var-item';
        varItem.innerHTML = `
          <span class="var-name">${name}</span>
          <span class="var-value">${this.formatValue(value)}</span>
        `;
        varsContainer.appendChild(varItem);
      }
      historyItem.appendChild(varsContainer);
    }
    
    // 添加到列表顶部（最新的在上面）
    varList.insertBefore(historyItem, varList.firstChild);
    
    // 高亮最新项
    historyItem.classList.add('current');
    
    // 移除其他项的高亮
    const allItems = varList.querySelectorAll('.history-item');
    allItems.forEach((item, index) => {
      if (index > 0) item.classList.remove('current');
    });
    
    // 限制显示数量（最多保疙60条）
    while (varList.children.length > 60) {
      varList.removeChild(varList.lastChild!);
    }
  }

  /**
   * 格式化值
   */
  private formatValue(value: unknown): string {
    if (typeof value === 'number') {
      return value.toString();
    } else if (typeof value === 'string') {
      return value;
    } else {
      return JSON.stringify(value);
    }
  }

  /**
   * 生成冒泡排序步骤
   */
  generateBubbleSortSteps(array: number[]): AnimationStep[] {
    const steps: AnimationStep[] = [];
    const arr = [...array];
    const n = arr.length;
    
    for (let i = 0; i < n - 1; i++) {
      for (let j = 0; j < n - i - 1; j++) {
        // 比较步骤
        steps.push({
          type: 'compare',
          indices: [j, j + 1],
          description: `比较 arr[${j}]=${arr[j]} 和 arr[${j + 1}]=${arr[j + 1]}`,
          variables: { i, j, 'arr[j]': arr[j], 'arr[j+1]': arr[j + 1] }
        });
        
        if (arr[j] > arr[j + 1]) {
          // 交换步骤
          [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
          steps.push({
            type: 'swap',
            indices: [j, j + 1],
            description: `交换位置: ${arr[j + 1]} ↔ ${arr[j]}`,
            variables: { i, j, '交换后': `[${arr.join(', ')}]` }
          });
        }
        
        // 取消高亮
        steps.push({
          type: 'unmark',
          indices: [j, j + 1],
          description: ''
        });
      }
      
      // 标记已排序
      steps.push({
        type: 'mark',
        indices: [n - i - 1],
        description: `位置 ${n - i - 1} 已排序完成`
      });
    }
    
    // 标记第一个元素
    steps.push({
      type: 'mark',
      indices: [0],
      description: '排序完成！'
    });
    
    return steps;
  }
}
